import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  MessageSquare, 
  Search, 
  AlertCircle,
  Clock,
  CheckCircle,
  X,
  Send
} from 'lucide-react';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  user_email: string;
  created_at: string;
  updated_at: string;
  replies: {
    id: string;
    message: string;
    user_email: string;
    created_at: string;
    is_internal: boolean;
  }[];
}

export function AdminSupportView() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [response, setResponse] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  async function fetchTickets() {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          replies:ticket_replies (
            id,
            message,
            created_at,
            user_email,
            is_internal
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('Fetched tickets:', data); // Debug log
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmitResponse = async () => {
    if (!selectedTicket || !response.trim()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('add_reply', {
        p_ticket_id: selectedTicket.id,
        p_message: response,
        p_is_internal: isInternal
      });

      if (error) throw error;

      // If not an internal note, update ticket status
      if (!isInternal) {
        const { error } = await supabase.rpc('update_ticket_status', {
          p_ticket_id: selectedTicket.id,
          p_status: 'waiting_for_customer'
        });
        if (error) throw error;
      }

      // Refresh ticket data
      await fetchTickets();
      setResponse('');
      setIsInternal(false);
    } catch (err) {
      console.error('Error submitting response:', err);
      setError('Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user?.email) {
        setError('Failed to get user information');
        return;
      }

      const { error } = await supabase.rpc('update_ticket_status', {
        p_ticket_id: ticketId,
        p_status: newStatus
      });

      if (error) throw error;
      await fetchTickets();
    } catch (err) {
      console.error('Error updating ticket status:', err);
      setError('Failed to update ticket status');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (!ticket) return false;
    
    const matchesSearch = !searchTerm || 
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user_email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { color: 'blue', icon: AlertCircle, text: 'Open' },
      in_progress: { color: 'yellow', icon: Clock, text: 'In Progress' },
      waiting_for_customer: { color: 'orange', icon: Clock, text: 'Waiting for Customer' },
      waiting_for_insurer: { color: 'purple', icon: Clock, text: 'Waiting for Insurer' },
      resolved: { color: 'green', icon: CheckCircle, text: 'Resolved' },
      closed: { color: 'gray', icon: CheckCircle, text: 'Closed' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || 
                  { color: 'gray', icon: AlertCircle, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <MessageSquare className="w-6 h-6 text-indigo-600 mr-2" />
          Support Tickets
        </h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-md"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-md px-3 py-2"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="waiting_for_customer">Waiting for Customer</option>
            <option value="waiting_for_insurer">Waiting for Insurer</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Tickets List */}
        <div className="col-span-1 bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredTickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                  selectedTicket?.id === ticket.id ? 'bg-gray-50' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-mono text-gray-500">
                    Ticket #{ticket.id.slice(0, 8)}
                  </span>
                  {getStatusBadge(ticket.status)}
                </div>
                <h3 className="font-medium text-gray-900 mb-1">{ticket.subject}</h3>
                <p className="text-sm text-gray-500 mb-2">{ticket.user_email}</p>
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  {new Date(ticket.updated_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Ticket Details */}
        <div className="col-span-2">
          {selectedTicket ? (
            <div className="bg-white rounded-lg shadow h-full flex flex-col">
              {/* Ticket Header */}
              <div className="p-6 border-b">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-1">
                      {selectedTicket.subject}
                    </h3>
                    <div className="flex items-center space-x-4">
                      <p className="text-sm text-gray-500">
                        Ticket #{selectedTicket.id.slice(0, 8)} • {selectedTicket.user_email}
                      </p>
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting_for_customer">Waiting for Customer</option>
                        <option value="waiting_for_insurer">Waiting for Insurer</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                  {getStatusBadge(selectedTicket.status)}
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="capitalize">{selectedTicket.priority} Priority</span>
                  <span>•</span>
                  <span>Created {new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {selectedTicket.replies?.map((reply) => (
                  <div
                    key={reply.id}
                    className={`rounded-lg p-4 ${
                      reply.is_internal 
                        ? 'bg-yellow-50 border border-yellow-100'
                        : reply.user_email === selectedTicket.user_email
                          ? 'bg-green-50 border border-green-100'
                          : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-900">
                        {reply.user_email}
                        {reply.is_internal && (
                          <span className="ml-2 text-xs font-medium text-yellow-600">
                            (Internal Note)
                          </span>
                        )}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(reply.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                  </div>
                ))}
              </div>

              {/* Response Form */}
              <div className="p-6 border-t bg-gray-50">
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded text-yellow-600 focus:ring-yellow-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Internal Note (only visible to admin)
                    </span>
                  </label>
                </div>
                <div className="flex space-x-4">
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder={isInternal ? "Add an internal note..." : "Type your response..."}
                    className="flex-1 p-3 border rounded-lg"
                    rows={3}
                  />
                  <button
                    onClick={handleSubmitResponse}
                    disabled={!response.trim() || submitting}
                    className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 self-end ${
                      isInternal 
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Ticket</h3>
              <p className="text-gray-500">Choose a ticket from the list to view its details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}